import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { getTenantConfig } from '@/lib/tenant-config'
import {
  Button,
  Input,
  Label,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  useToast,
} from '@lead-magnifier/ui'
import {
  Mail,
  Phone,
  Globe,
  FileText,
  Download,
  Pencil,
  Briefcase,
  User,
  ChevronDown,
  Lock,
  Loader2,
} from 'lucide-react'

export const Route = createFileRoute('/messetasche/$token/')({
  component: MessetaschePage,
})

interface ScannedExhibitor {
  id: string
  companyName: string
  companyDescription: string | null
  logoUrl: string | null
  bannerUrl: string | null
  websiteUrl: string | null
  personalMessage: string | null
  contactPersons: Array<{ name: string; role?: string; email?: string; phone?: string; photo_url?: string }>
  documents: Array<{ name: string; url: string; type?: string }>
  scannedAt: string
}

interface VisitorData {
  visitorId: string
  name: string | null
  email: string
  company: string | null
  doiConfirmed: boolean
  eventName: string
  eventDate: string
  eventLogo: string | null
  eventBanner: string | null
  eventColor: string
  exhibitors: ScannedExhibitor[]
}

function MessetaschePage() {
  const { token } = Route.useParams()
  const tenant = getTenantConfig()
  const { toast } = useToast()

  const [data, setData] = useState<VisitorData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [editName, setEditName] = useState('')
  const [editEmail, setEditEmail] = useState('')
  const [editCompany, setEditCompany] = useState('')
  const [saving, setSaving] = useState(false)

  const detailRefs = useRef<Record<string, HTMLDivElement | null>>({})

  useEffect(() => { loadData() }, [token])

  async function loadData() {
    try {
      const { data: visitor, error: visitorError } = await supabase
        .from('visitors')
        .select('id, name, email, company, doi_confirmed, events (name, start_date, end_date, logo_url, header_image_url, primary_color)')
        .eq('token', token)
        .single()

      if (visitorError || !visitor) { setError('Messetasche nicht gefunden'); setLoading(false); return }

      const event = visitor.events as unknown as {
        name: string; start_date: string; end_date: string;
        logo_url: string | null; header_image_url: string | null; primary_color: string
      }

      const { data: scans } = await supabase
        .from('visitor_scans')
        .select('id, scanned_at, event_exhibitors (id, user_id)')
        .eq('visitor_id', visitor.id)
        .order('scanned_at', { ascending: true })

      const userIds = (scans || [])
        .map((s) => (s.event_exhibitors as unknown as { user_id: string })?.user_id)
        .filter(Boolean)

      const { data: profiles } = userIds.length > 0
        ? await supabase.from('exhibitor_profiles').select('*').in('user_id', userIds)
        : { data: [] }

      const profileMap = new Map((profiles || []).map((p) => [p.user_id, p]))

      const exhibitors: ScannedExhibitor[] = (scans || []).map((scan) => {
        const ee = scan.event_exhibitors as unknown as { id: string; user_id: string }
        const profile = profileMap.get(ee?.user_id)
        return {
          id: scan.id,
          companyName: profile?.company_name || 'Aussteller',
          companyDescription: profile?.company_description || null,
          logoUrl: profile?.logo_url || null,
          bannerUrl: profile?.banner_url || null,
          websiteUrl: profile?.website_url || null,
          personalMessage: profile?.personal_message || null,
          contactPersons: (profile?.contact_persons as ScannedExhibitor['contactPersons']) || [],
          documents: (profile?.documents as ScannedExhibitor['documents']) || [],
          scannedAt: scan.scanned_at,
        }
      })

      const s = new Date(event.start_date)
      const e = new Date(event.end_date)
      const eventDate = s.toLocaleDateString('de-AT', { day: 'numeric', month: 'long' })
        + ' – ' + e.toLocaleDateString('de-AT', { day: 'numeric', month: 'long', year: 'numeric' })

      setData({
        visitorId: visitor.id, name: visitor.name, email: visitor.email,
        company: visitor.company, doiConfirmed: visitor.doi_confirmed || false,
        eventName: event.name, eventDate,
        eventLogo: event.logo_url, eventBanner: event.header_image_url,
        eventColor: event.primary_color || tenant.branding.primary_color, exhibitors,
      })
    } catch { setError('Fehler beim Laden') } finally { setLoading(false) }
  }

  async function handleSaveProfile() {
    if (!data) return
    setSaving(true)
    try {
      const { error } = await supabase.from('visitors')
        .update({ name: editName.trim() || null, email: editEmail.trim(), company: editCompany.trim() || null })
        .eq('id', data.visitorId)
      if (error) throw error
      setData({ ...data, name: editName.trim(), email: editEmail.trim(), company: editCompany.trim() })
      setEditOpen(false)
      toast({ title: 'Daten aktualisiert' })
    } catch { toast({ title: 'Fehler beim Speichern', variant: 'destructive' }) }
    finally { setSaving(false) }
  }

  function toggleExhibitor(id: string) {
    const next = expandedId === id ? null : id
    setExpandedId(next)
    if (next) {
      setTimeout(() => {
        detailRefs.current[next]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }, 100)
    }
  }

  if (loading) return (
    <div className="min-h-screen bg-neutral-50 p-4 pt-16">
      <div className="max-w-2xl mx-auto space-y-4">
        <Skeleton className="h-48 w-full rounded-2xl" />
        <Skeleton className="h-20 w-full rounded-2xl" />
        <Skeleton className="h-20 w-full rounded-2xl" />
      </div>
    </div>
  )

  if (error || !data) return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6">
      <div className="text-center">
        <Briefcase className="h-12 w-12 mx-auto text-neutral-300 mb-4" />
        <p className="text-neutral-600 text-base">{error}</p>
      </div>
    </div>
  )

  const brandColor = data.eventColor

  return (
    <div className="min-h-screen bg-neutral-50">

      {/* ── Header ────────────────────────── */}
      <header className="relative text-white overflow-hidden" style={{ backgroundColor: brandColor }}>
        {data.eventBanner && (
          <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${data.eventBanner})` }} />
        )}
        <div className="relative z-10 max-w-2xl mx-auto px-6 pt-10 pb-14 text-center">
          {data.eventLogo && (
            <img src={data.eventLogo} alt="" className="h-12 w-auto mx-auto mb-5 object-contain brightness-0 invert" />
          )}
          <p className="text-sm font-semibold text-white/70 uppercase tracking-widest">Ihr Messebesuch</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">{data.eventName}</h1>
          <p className="text-white/60 mt-2">{data.eventDate}</p>
        </div>
      </header>

      {/* ── Visitor Bar ───────────────────── */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 -mt-6 relative z-20">
        <div className="bg-white rounded-lg shadow-md border border-neutral-200/60 px-5 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-bold text-base truncate">{data.name || 'Messebesucher'}</p>
            <p className="text-sm text-neutral-500 truncate">
              {data.email}{data.company && ` · ${data.company}`}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm font-bold px-3 py-1.5 rounded text-white" style={{ backgroundColor: brandColor }}>
              {data.exhibitors.length}
            </span>
            <button
              onClick={() => { setEditName(data.name || ''); setEditEmail(data.email); setEditCompany(data.company || ''); setEditOpen(true) }}
              className="h-10 w-10 rounded-lg bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center transition-colors"
            >
              <Pencil className="h-4 w-4 text-neutral-600" />
            </button>
          </div>
        </div>
      </div>

      {/* ── DOI Banner ──────────────────── */}
      {!data.doiConfirmed && (
        <div className="max-w-2xl mx-auto px-4 sm:px-6 mt-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-5 py-4 flex items-start gap-3">
            <Mail className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-sm text-amber-800">E-Mail noch nicht bestätigt</p>
              <p className="text-sm text-amber-700 mt-0.5">
                Kontaktdaten und Dokumente werden freigeschaltet, sobald Sie Ihre E-Mail-Adresse bestätigt haben.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Exhibitor List ────────────────── */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 pb-16 space-y-3">

        {data.exhibitors.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-lg border border-neutral-200/60">
            <Briefcase className="h-12 w-12 mx-auto text-neutral-300 mb-4" />
            <p className="text-neutral-500 text-base">Noch keine Aussteller gescannt.</p>
          </div>
        ) : (
          data.exhibitors.map((ex) => {
            const isOpen = expandedId === ex.id
            const docsCount = ex.documents.length
            const contactCount = ex.contactPersons.length

            return (
              <div
                key={ex.id}
                ref={(el) => { detailRefs.current[ex.id] = el }}
                className="bg-white rounded-lg border border-neutral-200/60 shadow-sm overflow-hidden transition-shadow hover:shadow-md"
              >
                {/* ── Banner + Logo Header ── */}
                <button
                  onClick={() => toggleExhibitor(ex.id)}
                  className="w-full text-left"
                >
                  {/* Banner */}
                  <div className="relative h-32 sm:h-36 overflow-hidden">
                    {ex.bannerUrl ? (
                      <img src={ex.bannerUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0" style={{ backgroundColor: brandColor + '15' }} />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

                    {/* Logo overlay */}
                    {ex.logoUrl && (
                      <img src={ex.logoUrl} alt="" className="absolute bottom-3 left-4 h-10 w-10 rounded-md object-contain bg-white p-0.5 shadow" />
                    )}

                    {/* Chevron */}
                    <div className="absolute top-3 right-3 h-7 w-7 rounded-md bg-white/80 backdrop-blur flex items-center justify-center">
                      <ChevronDown className={`h-4 w-4 text-neutral-600 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    </div>
                  </div>

                  {/* Title bar */}
                  <div className="px-5 py-4">
                    <h2 className="text-lg font-bold tracking-tight">{ex.companyName}</h2>
                    <p className="text-sm text-neutral-500 mt-0.5 line-clamp-1">
                      {ex.companyDescription || ''}
                    </p>
                    <div className="flex gap-3 mt-2">
                      {contactCount > 0 && (
                        <span className="text-xs text-neutral-400 flex items-center gap-1">
                          <User className="h-3 w-3" />{contactCount} Kontakt{contactCount !== 1 ? 'e' : ''}
                        </span>
                      )}
                      {docsCount > 0 && (
                        <span className="text-xs text-neutral-400 flex items-center gap-1">
                          <FileText className="h-3 w-3" />{docsCount} Download{docsCount !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </button>

                {/* ── Expanded Details ──────────── */}
                {isOpen && (
                  <div className="border-t border-neutral-100 animate-fade-in-up">

                    {/* Description + Website */}
                    {(ex.companyDescription || ex.websiteUrl) && (
                      <div className="px-5 pt-4 pb-3">
                        {ex.companyDescription && (
                          <p className="text-base text-neutral-600 leading-relaxed">{ex.companyDescription}</p>
                        )}
                        {ex.websiteUrl && (
                          <a href={ex.websiteUrl} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm mt-2 hover:underline transition-colors"
                            style={{ color: brandColor }}>
                            <Globe className="h-4 w-4" />
                            {ex.websiteUrl.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                          </a>
                        )}
                      </div>
                    )}

                    {/* Personal Message */}
                    {ex.personalMessage && (
                      <div className="mx-5 mb-4 p-4 rounded-md border-l-2" style={{ backgroundColor: brandColor + '06', borderLeftColor: brandColor }}>
                        <p className="text-sm text-neutral-700 leading-relaxed">
                          {ex.personalMessage}
                        </p>
                      </div>
                    )}

                    {/* Contacts — 2-Spalten-Grid */}
                    {ex.contactPersons.length > 0 && (
                      <div className="px-5 pb-4 grid grid-cols-2 gap-3">
                        {ex.contactPersons.map((cp, i) => (
                          <div key={i} className="bg-neutral-50 rounded-md p-4 text-center">
                            {cp.photo_url ? (
                              <img src={cp.photo_url} alt={cp.name} className="h-20 w-20 rounded-md object-cover mx-auto mb-3" />
                            ) : (
                              <div className="h-20 w-20 rounded-md bg-neutral-200 flex items-center justify-center mx-auto mb-3">
                                <User className="h-8 w-8 text-neutral-400" />
                              </div>
                            )}
                            <p className="font-bold text-sm">{cp.name}</p>
                            {cp.role && <p className="text-xs text-neutral-500 mt-0.5">{cp.role}</p>}
                            {data.doiConfirmed ? (
                              <div className="flex flex-col gap-1.5 mt-3">
                                {cp.email && (
                                  <a href={`mailto:${cp.email}`}
                                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded text-xs font-semibold text-white"
                                    style={{ backgroundColor: brandColor }}>
                                    <Mail className="h-3.5 w-3.5" />E-Mail
                                  </a>
                                )}
                                {cp.phone && (
                                  <a href={`tel:${cp.phone}`}
                                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded border border-neutral-200 text-xs font-semibold text-neutral-700 hover:bg-white">
                                    <Phone className="h-3.5 w-3.5" />Anrufen
                                  </a>
                                )}
                              </div>
                            ) : (
                              <p className="text-xs text-neutral-400 mt-3 flex items-center justify-center gap-1">
                                <Lock className="h-3 w-3" />E-Mail bestätigen
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Downloads */}
                    {ex.documents.length > 0 && (
                      <div className="border-t border-neutral-100 px-5 py-4 grid gap-2 sm:grid-cols-2">
                        {ex.documents.map((doc, i) => (
                          data.doiConfirmed ? (
                            <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-3 p-3 rounded-md bg-neutral-50 border border-neutral-100 hover:bg-neutral-100 transition-colors group">
                              <div className="h-10 w-10 rounded-md flex items-center justify-center shrink-0"
                                style={{ backgroundColor: brandColor + '10' }}>
                                <FileText className="h-5 w-5" style={{ color: brandColor }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold truncate">{doc.name}</p>
                                <p className="text-xs text-neutral-400 uppercase">{doc.type || 'PDF'}</p>
                              </div>
                              <Download className="h-4 w-4 text-neutral-300 group-hover:text-neutral-600 shrink-0" />
                            </a>
                          ) : (
                            <div key={i} className="flex items-center gap-3 p-3 rounded-md bg-neutral-50 border border-neutral-100 opacity-50">
                              <div className="h-10 w-10 rounded-md flex items-center justify-center shrink-0 bg-neutral-100">
                                <FileText className="h-5 w-5 text-neutral-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold truncate text-neutral-400">{doc.name}</p>
                                <p className="text-xs text-neutral-400 uppercase">{doc.type || 'PDF'}</p>
                              </div>
                              <Lock className="h-4 w-4 text-neutral-300 shrink-0" />
                            </div>
                          )
                        ))}
                      </div>
                    )}

                    {/* Timestamp */}
                    <div className="border-t border-neutral-100 px-5 py-2.5">
                      <p className="text-xs text-neutral-400">
                        Besucht am {new Date(ex.scannedAt).toLocaleString('de-AT', {
                          day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )
          })
        )}

        <p className="text-center text-sm text-neutral-300 pt-8">{tenant.branding.name}</p>
      </div>

      {/* ── Edit Dialog ───────────────────── */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Deine Daten bearbeiten</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>E-Mail</Label>
              <Input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>Firma</Label>
              <Input value={editCompany} onChange={(e) => setEditCompany(e.target.value)} className="h-11" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSaveProfile} disabled={!editEmail.trim() || saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
